import { useTranslation } from 'react-i18next';
import { ChartNoAxesColumn, Check, Image as ImageIcon, MapPin, Type, type LucideIcon } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../ui/dialog';
import type { StickerKind } from './stickers';

const ICONS: Record<StickerKind, LucideIcon> = { card: ImageIcon, gym: MapPin, name: Type, stats: ChartNoAxesColumn };
const LABEL_KEYS: Record<StickerKind, string> = {
  card: 'shareClimb.stickerCard',
  gym: 'shareClimb.stickerGym',
  name: 'shareClimb.stickerName',
  stats: 'shareClimb.stickerStats',
};
const HINT_KEYS: Record<StickerKind, string> = {
  card: 'shareClimb.stickerCardHint',
  gym: 'shareClimb.stickerGymHint',
  name: 'shareClimb.stickerNameHint',
  stats: 'shareClimb.stickerStatsHint',
};

interface StickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The stickers offered: a climb without a photo has no card. */
  kinds: StickerKind[];
  /** The ones already on the media, shown ticked. */
  present: StickerKind[];
  onPick: (kind: StickerKind) => void;
}

/**
 * The stickers to lay over the media, from the editor's side column. One of
 * each at most: one already there is shown ticked. Picking one closes the
 * dialog; the sticker is then on the media, to be moved and sized.
 */
export function StickerDialog({ open, onOpenChange, kinds, present, onPick }: StickerDialogProps) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="sticker-dialog">
        <DialogHeader>
          <DialogTitle>{t('shareClimb.stickers')}</DialogTitle>
          <DialogDescription>{t('shareClimb.stickersHint')}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          {kinds.map((kind) => {
            const Icon = ICONS[kind];
            const disabled = present.includes(kind);
            return (
              <button
                key={kind}
                type="button"
                disabled={disabled}
                onClick={() => { onPick(kind); onOpenChange(false); }}
                className="flex items-center gap-3 rounded-lg border p-3 text-left transition-colors hover:bg-accent disabled:pointer-events-none disabled:opacity-50"
                data-testid={`sticker-${kind}`}
              >
                <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Icon className="size-5" aria-hidden />
                </span>
                <span className="flex flex-1 flex-col">
                  <span className="text-sm font-medium">{t(LABEL_KEYS[kind])}</span>
                  <span className="text-xs text-muted-foreground">{t(HINT_KEYS[kind])}</span>
                </span>
                {disabled && <Check className="size-4 shrink-0 text-muted-foreground" aria-hidden />}
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
